import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Edit } from "lucide-react";
import { useLevelBrowserPolicies } from "@/hooks/useLevelBrowserPolicies";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";

interface LevelBrowserCategoriesContentProps {
  levelId: string;
}

export const LevelBrowserCategoriesContent = ({ levelId }: LevelBrowserCategoriesContentProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const { categories } = useWebsiteCategories();
  const { browserPolicy, updateBrowserPolicy } = useLevelBrowserPolicies(levelId);

  const getCategoryStatus = (categoryName: string) => {
    // Check if category is in the browser policy's categories array
    // Reversed logic: categories in the array are blocked, not in array are allowed
    if (browserPolicy?.categories.includes(categoryName)) {
      return 'Blocked';
    }
    return 'Allowed';
  };

  const getRowBackgroundColor = (status: string) => {
    if (status === 'Allowed') {
      return 'bg-green-50 hover:bg-green-100';
    } else if (status === 'Blocked') {
      return 'bg-red-50 hover:bg-red-100';
    }
    return 'hover:bg-gray-50';
  };

  const handleStatusChangeLocal = (categoryName: string, newStatus: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [categoryName]: newStatus
    }));
  };

  const handleSaveChanges = async () => {
    try {
      // Build the new categories array based on pending changes
      // Reversed logic: categories array now contains blocked categories
      const newBlockedCategories = categories
        .filter(category => {
          const pendingStatus = pendingChanges[category.name];
          if (pendingStatus) {
            return pendingStatus === 'blocked';
          }
          // If no pending change, keep current status
          return browserPolicy?.categories.includes(category.name);
        })
        .map(category => category.name);

      await updateBrowserPolicy({ categories: newBlockedCategories });
      setPendingChanges({});
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to save category changes:', error);
    }
  };

  const handleCancelChanges = () => {
    setPendingChanges({});
    setIsEditMode(false);
  };

  const handleAllowAll = async () => {
    try {
      // Allow all categories by setting categories array to empty (reversed logic)
      await updateBrowserPolicy({ categories: [] });
    } catch (error) {
      console.error('Failed to allow all categories:', error);
    }
  };

  const handleBlockAll = async () => {
    try {
      // Block all categories by adding all category names to the array
      const allCategoryNames = categories.map(category => category.name);
      await updateBrowserPolicy({ categories: allCategoryNames });
    } catch (error) {
      console.error('Failed to block all categories:', error);
    }
  };

  if (!browserPolicy) {
    return (
      <div className="space-y-3">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Website Categories</h3>
          <p className="text-sm text-gray-600">Manage website category permissions for this level</p>
        </div>
        <div className="flex gap-2">
          {!isEditMode && (
            <>
              <Button variant="outline" size="sm" onClick={handleAllowAll}>
                Allow All
              </Button>
              <Button variant="outline" size="sm" onClick={handleBlockAll}>
                Block All
              </Button>
            </>
          )}
          {isEditMode ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelChanges}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges} disabled={Object.keys(pendingChanges).length === 0}>
                Save Changes
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories
                .sort((a, b) => {
                  const statusA = getCategoryStatus(a.name);
                  const statusB = getCategoryStatus(b.name);
                  
                  // First sort by status (Allowed first, then Blocked)
                  if (statusA !== statusB) {
                    return statusA === 'Allowed' ? -1 : 1;
                  }
                  
                  // Then sort alphabetically by name within the same status
                  return a.name.localeCompare(b.name);
                })
                .map((category) => {
                const status = getCategoryStatus(category.name);
                const pendingStatus = pendingChanges[category.name];
                const currentStatus = pendingStatus ? (pendingStatus === 'allowed' ? 'Allowed' : 'Blocked') : status;
                
                return (
                  <TableRow key={category.id} className={getRowBackgroundColor(currentStatus)}>
                    <TableCell>
                      <div className="font-medium">{category.name}</div>
                    </TableCell>
                    <TableCell>
                      {isEditMode ? (
                         <Select 
                          value={pendingChanges[category.name] || (status === 'Allowed' ? 'allowed' : 'blocked')} 
                          onValueChange={value => handleStatusChangeLocal(category.name, value)}
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white z-50">
                            <SelectItem value="allowed">
                              <div className="flex items-center justify-between w-full">
                                <span>Allowed</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="blocked">
                              <div className="flex items-center justify-between w-full">
                                <span>Blocked</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant={currentStatus === 'Allowed' ? 'success' : 'destructive'}>
                            {currentStatus}
                          </Badge>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isEditMode && (
        <div className="text-xs text-gray-500 p-3 bg-blue-50 rounded-lg">
          <p><strong>Edit Mode:</strong> Use the dropdowns to change category permissions for this level. 
          Click "Save Changes" to apply your changes or "Cancel" to discard them.</p>
        </div>
      )}
    </div>
  );
};
